import { Slider } from "@heroui/react";
import { CanvasLib } from "@sc07-canvas/lib/canvas";
import { useCallback, useEffect, useRef, useState } from "react";
import useSound from "use-sound";

import { useAppContext } from "@/contexts/AppContext";
import { useAudio } from "@/contexts/AudioContext";
import { CanvasCore } from "@/lib/canvas";

import network from "../../lib/network";
import African1 from "../../sounds/African1.mp3";
import African2 from "../../sounds/African2.mp3";
import African3 from "../../sounds/African3.mp3";
import African4 from "../../sounds/African4.mp3";
import Camera from "../../sounds/Camera.mp3";
import Coffee1 from "../../sounds/Coffee1.mp3";
import Coffee2 from "../../sounds/Coffee2.mp3";
import Retro6 from "../../sounds/Retro6.mp3";
import WoodBlock1 from "../../sounds/Wood Block1.mp3";
import WoodBlock2 from "../../sounds/Wood Block2.mp3";
import WoodBlock3 from "../../sounds/Wood Block3.mp3";
import { Switch } from "../core/Switch";

const _ = (v: boolean) => (v ? "enable" : "disable");
const DEFAULT_PIXEL_BUCKET_THRESHOLD = 6;

export const AudioSettings = () => {
  const { state, dispatch } = useAudio();
  const { config } = useAppContext();

  const coffee2Ref = useRef<(() => void) | null>(null);
  const delayedAvailableSoundRef = useRef<number | undefined>(undefined);
  const earlyAvailableSoundPlayedRef = useRef(false);
  const earlyBucketSoundPlayedRef = useRef(false);

  const [Coffee1Sound] = useSound(Coffee1, { volume: 0.5 });

  const [Coffee2Sound] = useSound(Coffee2, {
    volume: 0.5,
  });

  const [African1Sound] = useSound(African1, { volume: 0.5 });

  const [African2Sound] = useSound(African2, { volume: 0.5 });

  const [African3Sound] = useSound(African3, { volume: 0.5 });

  const [African4Sound] = useSound(African4, { volume: 0.5 });
  const [CameraSound] = useSound(Camera, { volume: 0.25 });

  const [Retro6Sound] = useSound(Retro6, { volume: 0.3 });

  const [WoodBlock1Sound] = useSound(WoodBlock1, { volume: 0.1 });

  const [WoodBlock2Sound] = useSound(WoodBlock2, { volume: 0.1 });

  const [WoodBlock3Sound] = useSound(WoodBlock3, { volume: 0.1 });

  const [pixelsAvailable, setPixelsAvailable] = useState(0);
  const [pixelAvailableOffset, setPixelAvailableOffset] = useState(0);
  const [pixelBucketThreshold, setPixelBucketThreshold] = useState(6);
  const [pixelBucketOffset, setPixelBucketOffset] = useState(0);
  const [pixelGainStartedAt, setPixelGainStartedAt] = useState<
    number | undefined
  >(() => network.getState("pixelLastPlaced")?.[0]);
  const [bucketReachedAt, setBucketReachedAt] = useState<number | undefined>(
    undefined,
  );

  const WoodSounds = [WoodBlock1Sound, WoodBlock2Sound, WoodBlock3Sound];

  useEffect(() => {
    coffee2Ref.current = Coffee2Sound;
  }, [Coffee2Sound]);

  useEffect(() => {
    return () => {
      if (delayedAvailableSoundRef.current !== undefined) {
        window.clearTimeout(delayedAvailableSoundRef.current);
        delayedAvailableSoundRef.current = undefined;
      }
    };
  }, [pixelAvailableOffset, state.pixelAvailable]);

  useEffect(() => {
    const handlePixelGainStarted = (time: number) => {
      setPixelGainStartedAt(time);
    };

    network.on("pixelLastPlaced", handlePixelGainStarted);

    return () => {
      network.off("pixelLastPlaced", handlePixelGainStarted);
    };
  }, []);

  const handleAvailable = useCallback(
    (available: number, isUndo: boolean) => {
      if (available > pixelsAvailable && state.pixelAvailable && !isUndo) {
        if (pixelAvailableOffset < 0) {
          if (!earlyAvailableSoundPlayedRef.current) Coffee2Sound();
          earlyAvailableSoundPlayedRef.current = false;
        } else if (pixelAvailableOffset === 0) {
          Coffee2Sound();
        } else {
          delayedAvailableSoundRef.current = window.setTimeout(() => {
            Coffee2Sound();
            delayedAvailableSoundRef.current = undefined;
          }, pixelAvailableOffset * 1000);
        }
      } else if (available < pixelsAvailable) {
        earlyAvailableSoundPlayedRef.current = false;
      }

      if (
        state.pixelBucketFull &&
        !isUndo &&
        available >= pixelBucketThreshold &&
        pixelsAvailable < pixelBucketThreshold
      ) {
        if (pixelBucketOffset < 0) {
          if (!earlyBucketSoundPlayedRef.current) Retro6Sound();
          earlyBucketSoundPlayedRef.current = false;
        } else {
          setBucketReachedAt(Date.now());
        }
      } else if (isUndo || available < pixelsAvailable) {
        earlyBucketSoundPlayedRef.current = false;
      }

      setPixelsAvailable(available);
    },
    [
      Retro6Sound,
      Coffee2Sound,
      pixelAvailableOffset,
      pixelBucketOffset,
      pixelBucketThreshold,
      pixelsAvailable,
      state.pixelAvailable,
      state.pixelBucketFull,
    ],
  );

  useEffect(() => {
    network.socket.on("availablePixels", handleAvailable);

    return () => {
      network.socket.off("availablePixels", handleAvailable);
    };
  }, [handleAvailable]);

  useEffect(() => {
    earlyAvailableSoundPlayedRef.current = false;

    if (
      !state.pixelAvailable ||
      pixelAvailableOffset >= 0 ||
      !config ||
      pixelsAvailable >= config.canvas.pixel.maxStack
    ) {
      return;
    }

    const canvas = CanvasCore.get(false);
    if (!canvas?.lastPlace) return;

    const cooldown = CanvasLib.getPixelCooldown(config);
    const availableAt = canvas.lastPlace + cooldown * 1000;
    const soundAt = availableAt + pixelAvailableOffset * 1000;
    const delay = soundAt - Date.now();

    if (delay <= 0) return;

    const timeout = window.setTimeout(() => {
      earlyAvailableSoundPlayedRef.current = true;
      Coffee2Sound();
    }, delay);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [
    Coffee2Sound,
    config,
    pixelAvailableOffset,
    pixelsAvailable,
    state.pixelAvailable,
  ]);

  useEffect(() => {
    earlyBucketSoundPlayedRef.current = false;

    if (!state.pixelBucketFull || !config) return;

    let soundAt: number;
    let clearReachedAt = false;

    if (pixelBucketOffset >= 0) {
      if (bucketReachedAt === undefined) return;

      soundAt = bucketReachedAt + pixelBucketOffset * 1000;
      clearReachedAt = true;
    } else {
      if (
        pixelGainStartedAt === undefined ||
        pixelsAvailable >= pixelBucketThreshold
      ) {
        return;
      }

      let cooldownSeconds = 0;
      for (
        let pixel = pixelsAvailable + 1;
        pixel <= pixelBucketThreshold;
        pixel += 1
      ) {
        cooldownSeconds += CanvasLib.getPixelCooldown(config);
      }

      soundAt =
        pixelGainStartedAt +
        cooldownSeconds * 1000 +
        pixelBucketOffset * 1000;
      if (soundAt <= Date.now()) return;
    }

    const timeout = window.setTimeout(() => {
      Retro6Sound();
      if (pixelBucketOffset < 0) earlyBucketSoundPlayedRef.current = true;
      if (clearReachedAt) setBucketReachedAt(undefined);
    }, Math.max(0, soundAt - Date.now()));

    return () => {
      window.clearTimeout(timeout);
    };
  }, [
    Retro6Sound,
    bucketReachedAt,
    config,
    pixelBucketOffset,
    pixelBucketThreshold,
    pixelGainStartedAt,
    pixelsAvailable,
    state.pixelBucketFull,
  ]);

  const handlePlace = useCallback(() => {
    if (!state.place) return;

    Coffee1Sound();
  }, [state.place]);

  useEffect(() => {
    network.on("placeSuccessful", handlePlace);

    return () => {
      network.off("placeSuccessful", handlePlace);
    };
  }, [handlePlace]);

  const handleUndo = useCallback(
    ({ available }: { available: boolean }) => {
      if (!state.pixelUndo) return;

      // undo state becoming unavailable means it was used
      if (!available) {
        African1Sound();
      }
    },
    [state.pixelUndo],
  );

  useEffect(() => {
    network.socket.on("undo", handleUndo);

    return () => {
      network.socket.off("undo", handleUndo);
    };
  }, [handleUndo]);

  const handleGlobalPlace = useCallback(() => {
    if (!state.globalPlace) return;

    const variant = Math.floor(Math.random() * WoodSounds.length);
    WoodSounds[variant]();
  }, [state.globalPlace]);

  useEffect(() => {
    network.socket.on("pixel", handleGlobalPlace);

    return () => {
      network.socket.off("pixel", handleGlobalPlace);
    };
  }, [handleGlobalPlace]);

  const handleDisconnect = useCallback(() => {
    if (!state.disconnected) return;

    African2Sound();
  }, [state.disconnected]);

  useEffect(() => {
    network.socket.on("disconnect", handleDisconnect);

    return () => {
      network.socket.off("disconnect", handleDisconnect);
    };
  }, [handleDisconnect]);

  const handleReconnect = useCallback(() => {
    if (!state.reconnected) return;

    African4Sound();
  }, [state.reconnected]);

  useEffect(() => {
    network.socket.on("connect", handleReconnect);

    return () => {
      network.socket.off("connect", handleReconnect);
    };
  }, [handleReconnect]);

  return (
    <div className="flex flex-col gap-4 p-2">
      <header className="flex flex-col gap-2">
        <h2 className="text-xl">Audio</h2>
        <p className="text-xs text-default-600">Sounds that play on events</p>
      </header>
      <section className="flex flex-col gap-2">
        <Switch
          silent
          isSelected={state.pixelAvailable}
          onValueChange={(v) => {
            dispatch([_(v), "pixelAvailable"]);
            if (v) {
              Coffee2Sound();
            }
          }}
        >
          Pixel Available
        </Switch>
        {state.pixelAvailable && (
          <Slider
            className="px-2"
            label="Chime Offset"
            step={1}
            minValue={-5}
            maxValue={15}
            value={-pixelAvailableOffset}
            onChange={(value) =>
              setPixelAvailableOffset(-(value as number))
            }
            getValue={(value) => {
              const offset = -(value as number);
              return `${offset > 0 ? "+" : ""}${offset}s`;
            }}
          />
        )}
        <Switch
          silent
          isSelected={state.pixelBucketFull}
          onValueChange={(v) => {
            dispatch([_(v), "pixelBucketFull"]);
            setBucketReachedAt(undefined);
            if (v) {
              Retro6Sound();
            }
          }}
        >
          Pixel Bucket Full
        </Switch>
        {state.pixelBucketFull && (
          <div className="flex flex-col gap-4 px-2 pb-2">
            <Slider
              label="Bucket Alert Level"
              step={1}
              minValue={1}
              maxValue={
                config?.canvas.pixel.maxStack ?? DEFAULT_PIXEL_BUCKET_THRESHOLD
              }
              value={pixelBucketThreshold}
              onChange={(value) => {
                setBucketReachedAt(undefined);
                setPixelBucketThreshold(value as number);
              }}
              getValue={(value) => {
                const pixels = value as number;
                return `${pixels} pixel${pixels === 1 ? "" : "s"}`;
              }}
            />
            <Slider
              label="Chime Offset"
              step={1}
              minValue={-5}
              maxValue={15}
              value={-pixelBucketOffset}
              onChange={(value) =>
                setPixelBucketOffset(-(value as number))
              }
              getValue={(value) => {
                const offset = -(value as number);
                return `${offset > 0 ? "+" : ""}${offset}s`;
              }}
            />
          </div>
        )}
        <Switch
          silent
          isSelected={state.place}
          onValueChange={(v) => {
            dispatch([_(v), "place"]);
            if (v) {
              Coffee1Sound();
            }
          }}
        >
          Pixel Place
        </Switch>
        <Switch
          silent
          isSelected={state.pixelUndo}
          onValueChange={(v) => {
            dispatch([_(v), "pixelUndo"]);
            if (v) {
              African1Sound();
            }
          }}
        >
          Pixel Undo
        </Switch>
        <Switch
          silent
          isSelected={state.uiClick}
          onValueChange={(v) => {
            dispatch([_(v), "uiClick"]);
            if (v) {
              African3Sound();
            }
          }}
        >
          UI Click
        </Switch>
        <Switch
          silent
          isSelected={state.cameraShutter}
          onValueChange={(v) => {
            dispatch([_(v), "cameraShutter"]);
            if (v) {
              CameraSound();
            }
          }}
        >
          Camera Shutter
        </Switch>
        <Switch
          silent
          isSelected={state.disconnected}
          onValueChange={(v) => {
            dispatch([_(v), "disconnected"]);
            if (v) {
              African2Sound();
            }
          }}
        >
          Disconnect
        </Switch>
        <Switch
          silent
          isSelected={state.reconnected}
          onValueChange={(v) => {
            dispatch([_(v), "reconnected"]);
            if (v) {
              African4Sound();
            }
          }}
        >
          Reconnect
        </Switch>
        <Switch
          silent
          isSelected={state.globalPlace}
          onValueChange={(v) => {
            dispatch([_(v), "globalPlace"]);
            if (v) {
              WoodSounds[Math.floor(Math.random() * WoodSounds.length)]();
            }
          }}
        >
          Global Pixel Place ⚠️
        </Switch>
      </section>
    </div>
  );
};
